import { useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useCountUp } from "../hooks/useCountUp";
import { MagneticButton } from "./MagneticButton";
import { EngineeringMotion } from "./EngineeringMotion";
import "./Hero.css";

const roles = ["Full Stack Developer", "AI Product Builder", "Cloud-Native Architect", "React + Java Specialist"];

// Expo.out easing — premium feel per UI Pro Max Modern Dark spec
const EASE = [0.16, 1, 0.3, 1];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

export function Hero() {
  const typingRef = useRef(null);
  const roleIndex = useRef(0);
  const charIndex = useRef(0);
  const isDeleting = useRef(false);
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true });

  useEffect(() => {
    let timeout;
    const type = () => {
      const current = roles[roleIndex.current];
      if (isDeleting.current) { charIndex.current--; }
      else { charIndex.current++; }
      if (typingRef.current) {
        typingRef.current.textContent = current.substring(0, charIndex.current);
      }
      let delay = isDeleting.current ? 50 : 90;
      if (!isDeleting.current && charIndex.current === current.length) {
        delay = 2200; isDeleting.current = true;
      } else if (isDeleting.current && charIndex.current === 0) {
        isDeleting.current = false;
        roleIndex.current = (roleIndex.current + 1) % roles.length;
        delay = 400;
      }
      timeout = setTimeout(type, delay);
    };
    timeout = setTimeout(type, 900);
    return () => clearTimeout(timeout);
  }, []);

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  const statValues = ["7+", "8", "1.5M+", "32%"];
  const statLabels = ["Years Exp.", "Engineers Led", "Txns/Day", "Faster Approvals"];

  const count0 = useCountUp(statValues[0], statsInView);
  const count1 = useCountUp(statValues[1], statsInView);
  const count2 = useCountUp(statValues[2], statsInView);
  const count3 = useCountUp(statValues[3], statsInView);
  const counts = [count0, count1, count2, count3];

  return (
    <section className="hero" id="home">
      {/* Ambient background */}
      <div className="hero__bg">
        <div className="hero__grid" />
        <div className="hero__noise" />
      </div>

      <div className="hero__content section-container">
        {/* Text side — staggered Framer Motion entrance */}
        <motion.div
          className="hero__text"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="hero__greeting" variants={itemVariants}>
            <span className="hero__greeting-line" />
            <span>Hi, I'm Deepak</span>
          </motion.div>

          <motion.h1 className="hero__name" variants={itemVariants}>
            I design and build <span className="gradient-text">digital experiences</span> that scale
          </motion.h1>

          <motion.div className="hero__role" variants={itemVariants}>
            <span ref={typingRef} className="hero__role-text" />
            <span className="hero__cursor">|</span>
          </motion.div>

          <motion.p className="hero__desc" variants={itemVariants}>
            A Sydney-based full stack developer turning complex ideas into
            fast, reliable products with React, Java Spring Boot, AWS, and AI.
          </motion.p>

          <motion.div className="hero__actions" variants={itemVariants}>
            <MagneticButton
              className="hero__btn hero__btn--primary"
              onClick={() => scrollTo("#projects")}
            >
              Explore Work
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </MagneticButton>
            <MagneticButton
              className="hero__btn hero__btn--glass"
              onClick={() => scrollTo("#contact")}
            >
              Get In Touch
            </MagneticButton>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero__visual"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: EASE, delay: 0.4 }}
        >
          <EngineeringMotion />
        </motion.div>

        <motion.div className="hero__stats" variants={itemVariants} ref={statsRef}>
          {statLabels.map((label, i) => (
            <div key={label} style={{ display: "contents" }}>
              {i > 0 && <div className="hero__stat-divider" />}
              <div className="hero__stat">
                <span className="hero__stat-value">{counts[i]}</span>
                <span className="hero__stat-label">{label}</span>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
